import { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { log } from '../lib/logger';

/**
 * Real-time Campaign Status Hook
 *
 * Subscribes to real-time updates for a specific campaign.
 * Automatically updates when status changes due to deliverables or invoice modifications.
 *
 * Usage:
 * ```tsx
 * const { status, loading, error, isConnected } = useCampaignRealtime(campaignId);
 * ```
 */
export function useCampaignRealtime(campaignId: string | null) {
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!campaignId) {
      setLoading(false);
      setIsConnected(false);
      return;
    }

    let channel: RealtimeChannel;

    const setupSubscription = async () => {
      try {
        setError(null);

        // Fetch initial status
        const { data, error: fetchError } = await supabase
          .from('campaigns')
          .select('campaign_status')
          .eq('id', campaignId)
          .maybeSingle();

        if (fetchError) throw fetchError;

        if (data) {
          setStatus(data.campaign_status);
        }
        setLoading(false);

        // Subscribe to real-time updates
        channel = supabase
          .channel(`campaign:${campaignId}`)
          .on(
            'postgres_changes',
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'campaigns',
              filter: `id=eq.${campaignId}`,
            },
            (payload) => {
              if (payload.new && 'campaign_status' in payload.new) {
                setStatus(payload.new.campaign_status as string);
              }
            }
          )
          .subscribe((status) => {
            if (status === 'SUBSCRIBED') {
              setIsConnected(true);
              setError(null);
            } else if (status === 'CHANNEL_ERROR') {
              setIsConnected(false);
              setError('Real-time connection failed');
              log.error('Campaign subscription error:', status);
            } else if (status === 'TIMED_OUT') {
              setIsConnected(false);
              setError('Connection timed out');
              log.error('Campaign subscription timed out');
            } else if (status === 'CLOSED') {
              setIsConnected(false);
            }
          });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load campaign status';
        log.error('Error setting up campaign realtime subscription:', err);
        setError(errorMessage);
        setLoading(false);
        setIsConnected(false);
      }
    };

    setupSubscription();

    return () => {
      if (channel) {
        setIsConnected(false);
        supabase.removeChannel(channel);
      }
    };
  }, [campaignId]);

  return { status, loading, error, isConnected };
}

/**
 * Real-time Campaign List Hook
 *
 * Subscribes to real-time updates for all campaigns.
 * Useful for dashboard views that need live status updates.
 *
 * Usage:
 * ```tsx
 * const { isConnected, error } = useCampaignsRealtime(
 *   (updatedCampaign) => {
 *     setCampaigns(prev => prev.map(c =>
 *       c.id === updatedCampaign.id ? { ...c, ...updatedCampaign } : c
 *     ));
 *   }
 * );
 * ```
 */
export function useCampaignsRealtime(
  onUpdate?: (campaign: any) => void,
  onInsert?: (campaign: any) => void,
  onDelete?: (campaignId: string) => void
) {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onUpdateRef = useRef(onUpdate);
  const onInsertRef = useRef(onInsert);
  const onDeleteRef = useRef(onDelete);
  onUpdateRef.current = onUpdate;
  onInsertRef.current = onInsert;
  onDeleteRef.current = onDelete;

  useEffect(() => {
    setError(null);

    const channel = supabase
      .channel('campaigns:all')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'campaigns',
        },
        (payload) => {
          if (onUpdateRef.current && payload.new) {
            onUpdateRef.current(payload.new);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'campaigns',
        },
        (payload) => {
          if (onInsertRef.current && payload.new) {
            onInsertRef.current(payload.new);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'campaigns',
        },
        (payload) => {
          if (onDeleteRef.current && payload.old) {
            onDeleteRef.current((payload.old as any).id);
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          setIsConnected(true);
          setError(null);
          log.debug('[Realtime] Campaigns subscription active');
        } else if (status === 'CHANNEL_ERROR') {
          setIsConnected(false);
          setError('Real-time connection failed');
          log.error('[Realtime] Campaign subscription error:', status);
        } else if (status === 'TIMED_OUT') {
          setIsConnected(false);
          setError('Connection timed out');
          log.error('[Realtime] Campaign subscription timed out');
        } else if (status === 'CLOSED') {
          setIsConnected(false);
          log.debug('[Realtime] Campaign subscription closed');
        }
      });

    return () => {
      setIsConnected(false);
      supabase.removeChannel(channel);
    };
  }, []);

  return { isConnected, error };
}
